import React, { useRef, useEffect, useState } from 'react'
import { BaseToothProps } from './types/tooth.types'
import { getToothConfig } from './config/tooth-config'
import {
  getToothSVGs,
  isToothSVGPreloaded,
  preloadToothSVGs,
} from './config/svg-registry'
import { useToothSVGManipulation } from './hooks/useToothSVGManipulation'

/** Generic tooth renderer for all tooth numbers and views. */
const BaseTooth = ({
  toothNumber,
  view,
  conditions,
  erosion,
  furcation,
  recession,
  crown,
  caries,
  recurringCaries,
  inlay,
  onlay,
  filling,
  impacted,
  drifted,
  bridge,
  attrition,
  abrasion,
  periradicular,
  crack,
  fracture,
  showMissingOverlay = true,
  showRetainedPrimaryOverlay = true,
}: BaseToothProps): React.ReactElement => {
  const svgRef = useRef<SVGSVGElement>(null)
  const [isLoaded, setIsLoaded] = useState(() =>
    isToothSVGPreloaded(toothNumber, view),
  )

  useEffect(() => {
    if (isToothSVGPreloaded(toothNumber, view)) {
      setIsLoaded(true)
      return undefined
    }
    setIsLoaded(false)
    let cancelled = false
    void preloadToothSVGs(toothNumber, view).then(() => {
      if (!cancelled) {
        setIsLoaded(true)
      }
    })
    return () => {
      cancelled = true
    }
  }, [toothNumber, view])

  const config = getToothConfig(toothNumber, view)

  useToothSVGManipulation({
    svgRef,
    view,
    conditions,
    erosion,
    caries,
    recurringCaries,
    inlay,
    onlay,
    filling,
    abrasion,
    furcation,
    bridge,
    periradicular,
    fracture,
    crack,
    crown,
    recession,
    attrition,
    impacted,
    drifted,
    showMissingOverlay,
    showRetainedPrimaryOverlay,
    config,
    isLoaded,
  })

  if (!isLoaded) {
    return (
      <svg
        viewBox="0 0 80 200"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', aspectRatio: '80 / 200', display: 'block' }}
      />
    )
  }

  const svgs = getToothSVGs(toothNumber, view)

  return (
    <svgs.Base
      ref={svgRef}
      style={{
        width: '100%',
        height: 'auto',
        display: 'block',
        overflow: 'visible',
      }}
    />
  )
}

export default React.memo(BaseTooth)
